/**
 * Iter XX.80 — Meta Pixel integration (existing Gully Dataset).
 *
 * Dataset / Pixel ID lives in `REACT_APP_META_PIXEL_ID`. This module
 * MUST use the ID as-is — never create a new Pixel/Dataset. If the
 * env var is missing the module short-circuits into a no-op so the
 * app never blocks on Meta.
 *
 * Public API mirrors the Clarity module's shape so the shared
 * `track()` facade in `lib/clarity.js` can fan out to both providers
 * with a single call. Callers never touch this module directly.
 *
 *   · init(pixelId?)          — one-shot async loader (idempotent)
 *   · track(name, meta?)      — trackCustom + standard-event mirror
 *   · setUser({user_id})      — advanced-matching (hashed by Meta)
 */

const _pixelIdEnv = process.env.REACT_APP_META_PIXEL_ID || "";
let _initTried = false;

// Iter XX.80 — Standard-event mirroring. Meta's ML optimisation works
// best when standard events are also fired. `trackCustom` is our
// primary channel per the founder brief (identical 8 events across
// Clarity + Meta) — but where a natural Meta standard event exists,
// we ALSO fire it with `track()` so ad audiences and conversion
// campaigns can target them without configuring custom conversions.
const STANDARD_EVENT_MIRROR = {
  landing_page_viewed:                 "PageView",
  // Iter XX.99 — Ads Manager needs a standard event to optimise
  // parent/player acquisition campaigns. `conversation_started` is
  // the earliest high-intent signal we produce (visitor sent their
  // first message to Gully) — canonically a Meta "Lead". The custom
  // event continues to fire for Clarity parity + funnel dashboards.
  conversation_started:                "Lead",
  // Iter XX.100 — Deeper-funnel optimisation signal. Fires when the
  // visitor has completed a real back-and-forth (≥3 user messages)
  // and Gully has provided coaching — the point at which the
  // conversation is meaningful, not a bounce. Mirrored to Meta's
  // standard `CompleteRegistration` so Ads Manager can optimise
  // campaigns for engaged conversations WITHOUT depending on
  // manually-configured Custom Conversions. The custom event
  // continues to fire for Clarity + funnel dashboards.
  first_meaningful_conversation_completed: "CompleteRegistration",
  checkout_started:                    "InitiateCheckout",
  gully_plus_purchased:                "Purchase",
};

function _hasFbq() {
  return typeof window !== "undefined"
    && typeof window.fbq === "function";
}

export function init(pixelId) {
  const id = (pixelId || _pixelIdEnv || "").trim();
  if (!id) return false;
  if (typeof window === "undefined") return false;
  if (_initTried) return _hasFbq();
  _initTried = true;

  try {
    // Meta's canonical async loader, converted from packed form.
    // eslint-disable-next-line func-names
    (function (f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod
          ? n.callMethod.apply(n, arguments)
          : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = "2.0";
      n.queue = [];
      t = b.createElement(e);
      t.async = true;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
    // Iter XX.80 — Explicit `set autoConfig false` is intentionally
    // skipped. We WANT Meta's auto-config to enrich standard events;
    // custom events are unaffected.
    window.fbq("init", id);
    // Fire a first PageView so Test Events show activity immediately.
    // Route-level PageView is also fired by MetaPixelProvider on
    // history changes — this one covers the initial paint.
    window.fbq("track", "PageView");
    return true;
  } catch {
    return false;
  }
}

export function track(name, meta) {
  if (!name) return;
  try {
    if (!_hasFbq()) return;
    const payload = _sanitize(meta);
    // Custom event — always fires. This is the primary channel for
    // the 8-event contract (matches Clarity 1:1).
    window.fbq("trackCustom", String(name), payload || undefined);
    // Standard event mirror — see STANDARD_EVENT_MIRROR above.
    const std = STANDARD_EVENT_MIRROR[name];
    if (std) {
      // `Purchase` requires {value, currency} for Meta to price the
      // conversion. Fall back to defaults so the pixel accepts it —
      // any ad-side reporting can be corrected later with server-side
      // Conversions API if precise attribution matters.
      if (std === "Purchase") {
        const p = {
          value: (meta && (meta.value != null ? Number(meta.value) : undefined)) ?? 0,
          currency: (meta && meta.currency) || "USD",
          ...(payload || {}),
        };
        window.fbq("track", std, p);
      } else if (std === "InitiateCheckout") {
        window.fbq("track", std, payload || undefined);
      } else {
        window.fbq("track", std);
      }
    }
  } catch { /* Meta is best-effort — never break the app */ }
}

// Iter XX.80 — Basic advanced matching. We DON'T pass raw email;
// Meta hashes on its side, but sending only a `external_id` (user_id)
// keeps us on the conservative privacy side.
export function setUser(user) {
  try {
    if (!_hasFbq() || !user) return;
    const uid = user.user_id || user.id;
    if (!uid) return;
    // Fire the "identify"-like signal via a set-userdata call.
    // fbq('set', 'user', ...) is not a public API; use trackCustom
    // with a stable tag so the Meta Events Manager surfaces it.
    window.fbq("trackCustom", "user_identified", { external_id: String(uid) });
  } catch { /* noop */ }
}

// Sanitize meta so Meta's pixel doesn't reject unusual values.
function _sanitize(meta) {
  if (!meta || typeof meta !== "object") return null;
  const out = {};
  Object.entries(meta).forEach(([k, v]) => {
    if (v == null) return;
    if (typeof v === "object") return;
    out[String(k)] = typeof v === "boolean" ? String(v) : v;
  });
  return Object.keys(out).length ? out : null;
}

export function pageView() {
  try { if (_hasFbq()) window.fbq("track", "PageView"); }
  catch { /* noop */ }
}
